import { useRouter } from 'next/router';

import { pricingPlans } from '@/components/store/config';

import type {
  BlogPostingSchema,
  BlogSchema,
  BreadcrumbListSchema,
  SeoHeadHook,
  SeoHeadHookProps,
  TenerifeGraphSchema,
} from './SeoHead.types';
import { SCHEMA_TYPE } from './SeoHead.types';

export const useSeoHead = ({
  blogSchema,
  breadcrumbSchema,
  metaDescription,
  noIndex,
  title,
}: SeoHeadHookProps): SeoHeadHook => {
  const router = useRouter();
  const pathname = router.asPath.split(/[?#]/)[0];
  const currentUrl = `${process.env.NEXT_PUBLIC_URL}${pathname}`;
  const description = metaDescription
    ? `${metaDescription}`
    : `${title} | Fox Paradox`;
  let robots: string;
  if (noIndex) {
    robots = 'noindex, nofollow';
  } else if (process.env.ENVIRONMENT === 'production') {
    robots = 'index, follow';
  } else {
    robots = 'noindex, nofollow';
  }

  const createBlogPostSchema = (): BlogPostingSchema => ({
    '@type': SCHEMA_TYPE.BLOG_POSTING,
    '@context': 'https://schema.org',
    author: [
      {
        '@type': 'Person',
        name: 'Fox Paradox',
        url: 'https://foxparadox.pl/o-mnie/',
      },
    ],
    dateModified: blogSchema.dateModified,
    datePublished: blogSchema.datePublished,
    headline: title,
    image: [blogSchema.mainImage],
    inLanguage: 'pl-PL',
    dateCreated: blogSchema.dateCreated,
    keywords: blogSchema.keywords,
    url: blogSchema.url,
    name: title,
    description,
  });

  const createBreadcrumbListSchema = (): BreadcrumbListSchema => ({
    '@context': 'https://schema.org',
    '@type': SCHEMA_TYPE.BREADCRUMB_LIST,
    itemListElement: breadcrumbSchema.breadcrumbElements?.map(
      (element, idx, arr) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: element.title,
        ...(idx + 1 !== arr.length && {
          item: element.breadcrumbUrl,
        }),
      }),
    ),
  });

  const createGeneralSchema = (): BlogSchema => ({
    '@type': SCHEMA_TYPE.BLOG,
    '@context': 'https://schema.org',
    '@id': 'https://foxparadox.pl/#website',
    url: 'https://foxparadox.pl/',
    name: 'Fox Paradox',
    description:
      'Blog o sposobach na tanie latanie, wartych uwagi miejscach, dobrym jedzeniu, sztuce i sposobach na motywację z rana! Wszystko w jednym miejscu!',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://foxparadox.pl/szukaj/?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
    inLanguage: 'pl-PL',
    isPartOf: {
      '@id': 'https://foxparadox.pl/#website',
    },
    about:
      'Treści o tematyce podróżniczej. Wskazówki na temat kupowania tanich lotów, planowania wycieczek, wypożyczania samochodu za granicą.',
    alternateName: 'FoxParadox',
  });

  const createTenerifeGraphSchema = (): TenerifeGraphSchema => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Book',
        '@id': 'https://foxparadox.pl/sklep/teneryfa',
        name: 'Teneryfa — Praktyczny przewodnik',
        alternateName: 'Teneryfa. Praktyczny przewodnik (eBook)',
        author: {
          '@type': 'Person',
          name: 'Magdalena Szabłowska',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Fox Paradox',
          url: 'https://foxparadox.pl',
        },
        datePublished: '2025-03-01',
        inLanguage: 'pl',
        bookFormat: 'https://schema.org/EBook',
        description:
          'Praktyczny przewodnik po Teneryfie: atrakcje, plaże, szlaki, plan podróży i porady dla podróżników.',
        image: 'https://foxparadox.pl/static/images/teneryfa/share.jpg',
        url: 'https://foxparadox.pl/sklep/teneryfa',
        isAccessibleForFree: false,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5.0',
          reviewCount: '4',
        },
        review: [
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Laura M.' },
            datePublished: '2025-04-15',
            reviewBody:
              'Jestem zachwycona tym przewodnikiem, bardzo ułatwił mi zaplanowanie wyjazdu na Teneryfę! Wszystkie atrakcje zebrane w jednym miejscu plus mega dużo poleceń restauracji, plaż i aktywności na wyspie. A do tego przepiękne opracowanie wizualne, polecam!',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5',
            },
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Elżbieta B.' },
            datePublished: '2025-04-16',
            reviewBody:
              'Bestseller na rynku przewodników turystycznych! Profesjonalne opracowanie, barwny język i najwyższej jakości zdjęcia sprawią, że od dziś będziesz planował podróż swojego życia! Number one you must have!!!',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5',
            },
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Bożena W.' },
            datePublished: '2025-04-17',
            reviewBody:
              'Czytanie przewodnika o Teneryfie było wspaniałą przygodą! Zajmujące treści ciekawie podane sprawiały, że odbywałam wirtualną podróż. Wyobraźnię podsycały wspaniałe zdjęcia, a także informacje praktyczne. To przewodnik dla każdego, kto jest ciekawy świata! Gorąco polecam!!',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5',
            },
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Beata Sz.' },
            datePublished: '2025-04-18',
            reviewBody:
              'Świetny, bardzo rzetelnie opracowany przewodnik. Jest w nim wszystko co potrzeba do zorganizowania sobie udanego pobytu na Teneryfie: praktyczne wskazówki, opis wielu atrakcji, noclegi, trasy piesze, plaże, mapki, ciekawostki kulturowe i kulinarne rekomendacje. Must have, szczerze polecam!',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5',
            },
          },
        ],
      },
      {
        '@type': 'Product',
        '@id': 'https://foxparadox.pl/sklep/teneryfa#product',
        name: 'Teneryfa — Praktyczny przewodnik (eBook)',
        description:
          'Praktyczny przewodnik po Teneryfie w wersji cyfrowej. Dostępny w trzech wariantach zakupu.',
        image: 'https://foxparadox.pl/static/images/teneryfa/share.jpg',
        sku: 'TENERYFA-EBOOK-2025',
        url: 'https://foxparadox.pl/sklep/teneryfa',
        brand: { '@type': 'Brand', name: 'Fox Paradox' },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5.0',
          reviewCount: '4',
        },
        review: [
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Laura M.' },
            datePublished: '2025-04-15',
            reviewBody:
              'Jestem zachwycona tym przewodnikiem, bardzo ułatwił mi zaplanowanie wyjazdu na Teneryfę! Wszystkie atrakcje zebrane w jednym miejscu plus mega dużo poleceń restauracji, plaż i aktywności na wyspie. A do tego przepiękne opracowanie wizualne, polecam!',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5',
            },
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Elżbieta B.' },
            datePublished: '2025-04-16',
            reviewBody:
              'Bestseller na rynku przewodników turystycznych! Profesjonalne opracowanie, barwny język i najwyższej jakości zdjęcia sprawią, że od dziś będziesz planował podróż swojego życia! Number one you must have!!!',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5',
            },
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Bożena W.' },
            datePublished: '2025-04-17',
            reviewBody:
              'Czytanie przewodnika o Teneryfie było wspaniałą przygodą! Zajmujące treści ciekawie podane sprawiały, że odbywałam wirtualną podróż. Wyobraźnię podsycały wspaniałe zdjęcia, a także informacje praktyczne. To przewodnik dla każdego, kto jest ciekawy świata! Gorąco polecam!!',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5',
            },
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Beata Sz.' },
            datePublished: '2025-04-18',
            reviewBody:
              'Świetny, bardzo rzetelnie opracowany przewodnik. Jest w nim wszystko co potrzeba do zorganizowania sobie udanego pobytu na Teneryfie: praktyczne wskazówki, opis wielu atrakcji, noclegi, trasy piesze, plaże, mapki, ciekawostki kulturowe i kulinarne rekomendacje. Must have, szczerze polecam!',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5',
            },
          },
        ],
        isRelatedTo: { '@id': 'https://foxparadox.pl/sklep/teneryfa' },
        offers: [
          {
            '@type': 'Offer',
            name: 'Plan Podstawowy',
            url: 'https://foxparadox.pl/sklep/teneryfa',
            priceCurrency: 'PLN',
            price: '89.00',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              priceCurrency: 'PLN',
              price: '89.00',
              referenceQuantity: {
                '@type': 'QuantitativeValue',
                value: 1,
                unitCode: 'EA',
              },
            },
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingRate: {
                '@type': 'MonetaryAmount',
                currency: 'PLN',
                value: '0.00',
              },
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'PL',
              },
            },
            availability: 'https://schema.org/InStock',
            priceValidUntil: '2026-12-31',
            description:
              "E-book 'Teneryfa. Praktyczny przewodnik' (170 stron). Bez planera podróży i bez spersonalizowanego planu.",
            seller: { '@type': 'Organization', name: 'Fox Paradox' },
            potentialAction: {
              '@type': 'BuyAction',
              target: pricingPlans[0].checkoutUrl,
            },
          },
          {
            '@type': 'Offer',
            name: 'Plan Rozszerzony',
            url: 'https://foxparadox.pl/sklep/teneryfa',
            priceCurrency: 'PLN',
            price: '99.00',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              priceCurrency: 'PLN',
              price: '99.00',
              referenceQuantity: {
                '@type': 'QuantitativeValue',
                value: 1,
                unitCode: 'EA',
              },
            },
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingRate: {
                '@type': 'MonetaryAmount',
                currency: 'PLN',
                value: '0.00',
              },
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'PL',
              },
            },
            availability: 'https://schema.org/InStock',
            priceValidUntil: '2026-12-31',
            description:
              "E-book 'Teneryfa. Praktyczny przewodnik' + uniwersalny planer podróży. Bez spersonalizowanego planu podróży.",
            seller: { '@type': 'Organization', name: 'Fox Paradox' },
            potentialAction: {
              '@type': 'BuyAction',
              target: pricingPlans[1].checkoutUrl,
            },
          },
          {
            '@type': 'Offer',
            name: 'Plan Premium',
            url: 'https://foxparadox.pl/sklep/teneryfa',
            priceCurrency: 'PLN',
            price: '249.00',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              priceCurrency: 'PLN',
              price: '249.00',
              referenceQuantity: {
                '@type': 'QuantitativeValue',
                value: 1,
                unitCode: 'EA',
              },
            },
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingRate: {
                '@type': 'MonetaryAmount',
                currency: 'PLN',
                value: '0.00',
              },
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'PL',
              },
            },
            availability: 'https://schema.org/InStock',
            priceValidUntil: '2026-12-31',
            description:
              "E-book 'Teneryfa. Praktyczny przewodnik' + uniwersalny planer podróży + spersonalizowany plan podróży po Teneryfie.",
            seller: { '@type': 'Organization', name: 'Fox Paradox' },
            potentialAction: {
              '@type': 'BuyAction',
              target: pricingPlans[2].checkoutUrl,
            },
          },
        ],
      },
    ],
  });
  return {
    createBreadcrumbListSchema,
    createBlogPostSchema,
    createGeneralSchema,
    createTenerifeGraphSchema,
    currentUrl,
    description: metaDescription,
    robots,
  };
};
